import { useCallback, useEffect, useState } from 'react';
import { useSelector } from '@xstate/react';

import { fetchEndpointResult } from 'api';
import type { FieldError } from 'components/ReactHookForm';
import { useCheckoutContext, useUserContext } from 'contexts';
import { useCallbackOnce } from 'hooks/useCallbackOnce';
import { useMarketCode } from 'hooks/useSitecoreContext';
import { useTemporaryStoredValue } from 'hooks/useStorage';
import type {
	BasketRowResponse,
	Cart,
	CartErrorType,
	ValidationErrorKey,
} from 'models/api';
import type { VariantId } from 'models/product';
import {
	type CheckoutMachineState,
	selectCartData,
	selectCartHasLoaded,
} from 'state-machines/checkout';
import type { CartId, VoyadoId } from 'types';
import {
	type IntrospectionResponse,
	isPaymentTypeWithComponentState,
} from 'utils/business-logic';
import { ignorePromiseRejection, is } from 'utils/helpers';
import { log } from 'utils/log';
import { snakeCase } from 'utils/string';

import type {
	GTMItemListId,
	GTMItemListName,
	GTMPromotionId,
	GTMPromotionName,
	GTMValidationErrorCode,
} from './GTMEvents';
import {
	getCartItemsWithPrice,
	getCartValueAndItems,
	getItemsWithPrice,
	getItemsWithPriceAndCurrency,
	getListItemProductData,
	getProductDataList,
	getPurchaseData,
	getWishlistTotalValue,
	type GTMListProduct,
	type GTMPriceProduct,
	type GTMPriceProductWithQuantity,
	MARKET_CURRENCIES,
	waitForServiceIdle,
} from './helpers';
import { useGtm } from './internal';

export function useViewItemListGTM() {
	const { pushToGTM } = useGtm();

	const sendViewItemListEvent = useCallback(
		(data: {
			itemListId: GTMItemListId;
			itemListName: GTMItemListName;
			pageSize: number;
			products: GTMListProduct[];
		}) => {
			pushToGTM({
				event: 'view_item_list',
				ecommerce: {
					items: getProductDataList(data),
				},
			});
		},
		[pushToGTM],
	);

	return { sendViewItemListEvent };
}

export function useAddToCartGTM() {
	const { pushToGTM } = useGtm();
	const marketCode = useMarketCode();

	const sendAddToCartEvent = useCallback(
		({
			cartId,
			items,
		}: {
			cartId: CartId;
			items: GTMPriceProductWithQuantity | GTMPriceProductWithQuantity[];
		}) => {
			pushToGTM({
				event: 'add_to_cart',
				ecommerce: {
					items: getItemsWithPriceAndCurrency(
						items,
						MARKET_CURRENCIES[marketCode],
					),
					cart_id: cartId,
				},
			});
		},
		[marketCode, pushToGTM],
	);

	return { sendAddToCartEvent };
}

function useAddToWishlistGTM() {
	const { pushToGTM } = useGtm();
	const marketCode = useMarketCode();

	const sendAddToWishlistEvent = useCallback(
		({
			itemListId,
			itemListName,
			product,
			quantity,
		}: {
			itemListId?: GTMItemListId;
			itemListName?: GTMItemListName;
			product: GTMPriceProduct;
			quantity: number;
		}) => {
			pushToGTM({
				event: 'add_to_wishlist',
				ecommerce: {
					value: getWishlistTotalValue(product, quantity),
					currency: MARKET_CURRENCIES[marketCode],
					items: getItemsWithPrice({
						product,
						quantity,
						itemListId,
						itemListName,
					}),
				},
			});
		},
		[marketCode, pushToGTM],
	);

	return { sendAddToWishlistEvent };
}

/**
 * Helpers for sending view_item_list, select_item and add_to_cart events
 * for product lists.
 */
export function useProductListGTMEvents(
	itemListId: GTMItemListId,
	itemListName: GTMItemListName,
) {
	const { pushToGTM } = useGtm();
	const { sendViewItemListEvent: sendViewItemListEventInternal } =
		useViewItemListGTM();
	const { sendAddToCartEvent: sendAddToCartEventInternal } = useAddToCartGTM();
	const { sendAddToWishlistEvent: sendAddToWishlistEventInternal } =
		useAddToWishlistGTM();
	const { setStoredValue } = useTemporaryStoredValue('GTM_LIST_DATA');

	const sendViewItemListEvent = useCallback(
		(products: GTMListProduct[], pageSize: number) => {
			if (products.length > 0 && pageSize > 0) {
				sendViewItemListEventInternal({
					products,
					pageSize,
					itemListId,
					itemListName,
				});
			}
		},
		[itemListId, itemListName, sendViewItemListEventInternal],
	);

	const sendSelectItemEvent = useCallback(
		(product: GTMListProduct, productIndex: number) => {
			pushToGTM({
				event: 'select_item',
				ecommerce: {
					items: [
						getListItemProductData({
							product,
							index: productIndex + 1,
							itemListId,
							itemListName,
						}),
					],
				},
			});
			setStoredValue({
				gtmItemListId: itemListId,
				gtmItemListName: itemListName,
			});
		},
		[itemListId, itemListName, pushToGTM, setStoredValue],
	);

	const sendAddToCartEvent = useCallback(
		(cartId: CartId, product: GTMPriceProduct, quantity: number = 1) => {
			sendAddToCartEventInternal({
				cartId,
				items: { product, quantity, itemListId, itemListName },
			});
		},
		[itemListId, itemListName, sendAddToCartEventInternal],
	);

	const sendAddToWishlistEvent = useCallback(
		(product: GTMPriceProduct, quantity: number = 1) => {
			sendAddToWishlistEventInternal({
				product,
				quantity,
				itemListId,
				itemListName,
			});
		},
		[itemListId, itemListName, sendAddToWishlistEventInternal],
	);

	const sendViewItemListEventOnce = useCallbackOnce(sendViewItemListEvent);

	return {
		sendViewItemListEvent,
		sendViewItemListEventOnce,
		sendSelectItemEvent,
		sendAddToCartEvent,
		sendAddToWishlistEvent,
		// Send them back out for convenience, here they're typed instead of
		// just plain strings.
		gtmItemListId: itemListId,
		gtmItemListName: itemListName,
	};
}

export function useProductGTMEvents() {
	const { pushToGTM } = useGtm();
	const { sendAddToCartEvent } = useAddToCartGTM();
	const { sendAddToWishlistEvent } = useAddToWishlistGTM();
	const marketCode = useMarketCode();

	const sendRemoveFromCartEvent = useCallback(
		(cartId: CartId, product: GTMPriceProduct, quantity: number) => {
			pushToGTM({
				event: 'remove_from_cart',
				ecommerce: {
					items: getItemsWithPriceAndCurrency(
						{
							product,
							quantity,
							itemListId: undefined,
							itemListName: undefined,
						},
						MARKET_CURRENCIES[marketCode],
					),
					cart_id: cartId,
				},
			});
		},
		[marketCode, pushToGTM],
	);

	return {
		sendAddToCartEvent,
		sendRemoveFromCartEvent,
		sendAddToWishlistEvent,
	};
}

function hasValidShippingInfo(checkoutState: CheckoutMachineState) {
	return !checkoutState.context?.data?.errorList?.some(
		(error) =>
			error.type === 'PickupLocationNotSelected' ||
			error.type === 'PickupLocationInvalid' ||
			error.type === 'DeliveryMethodNotSelected' ||
			error.type === 'DeliveryMethodInvalid',
	);
}

/**
 * The idea is to capture checkout events in as a funnel but send them once and when they have valid data.
 * since the checkout is open to changes in all parts right away
 * we have to make some assumptions to attempt to capture the events in the right order
 * if a valid shipping choice is done add_shipping_info is sent
 * if a valid payment choice is done add_payment_info is sent
 * if a default valid shipping choice is present and a payment choice is made we attempt to send both
 * if none are sent and payment is initiated we send both
 */
export function useCheckoutGTMEvents() {
	const { pushToGTM } = useGtm();
	const marketCode = useMarketCode();
	const { checkoutService } = useCheckoutContext();
	const isInitiatingPayment = useSelector(checkoutService, (state) =>
		state.hasTag('initiatingPayment'),
	);

	const hasCartLoaded = useSelector(checkoutService, selectCartHasLoaded);
	const cartData = useSelector(checkoutService, selectCartData);
	const shouldSendBeginCheckout = useSelector(
		checkoutService,
		(state) =>
			state.context.data?.checkoutStatus === 'Valid' ||
			state.context.data?.checkoutStatus === 'Invalid',
	);

	const [hasSentAddShippingInfo, setHasSentAddShippingInfo] = useState(false);
	const [hasSentAddPaymentInfo, setHasSentAddPaymentInfo] = useState(false);
	const [hasSentBeginCheckout, setHasSentBeginCheckout] = useState(false);

	useEffect(() => {
		if (
			hasCartLoaded &&
			cartData &&
			shouldSendBeginCheckout &&
			!hasSentBeginCheckout
		) {
			pushToGTM({
				event: 'begin_checkout',
				ecommerce: {
					...getCartValueAndItems({ cart: cartData, marketCode }),
				},
			});
			setHasSentBeginCheckout(true);
		}
	}, [
		cartData,
		hasCartLoaded,
		hasSentBeginCheckout,
		marketCode,
		pushToGTM,
		shouldSendBeginCheckout,
	]);

	const sendAddShippingInfoEvent = useCallback(
		(checkoutState: CheckoutMachineState) => {
			if (
				!hasSentAddShippingInfo &&
				hasValidShippingInfo(checkoutState) &&
				checkoutState.context.data
			) {
				pushToGTM({
					event: 'add_shipping_info',
					ecommerce: {
						shipping_tier:
							checkoutState.context.data.selectedDelivery?.deliveryMethodId ||
							'',
						...getCartValueAndItems({
							cart: checkoutState.context.data,
							marketCode,
						}),
					},
				});
				setHasSentAddShippingInfo(true);
			}
		},
		[hasSentAddShippingInfo, marketCode, pushToGTM],
	);

	const sendAddShippingInfoOnIdle = useCallback(async () => {
		const checkoutState = await waitForServiceIdle(checkoutService, (state) =>
			state.matches('idle'),
		);
		sendAddShippingInfoEvent(checkoutState);
	}, [checkoutService, sendAddShippingInfoEvent]);

	const sendAddShippingInfo = useCallback(() => {
		ignorePromiseRejection(sendAddShippingInfoOnIdle());
	}, [sendAddShippingInfoOnIdle]);

	const sendAddPaymentInfoOnIdle = useCallback(async () => {
		const checkoutState = await waitForServiceIdle(checkoutService, (state) =>
			state.matches('idle'),
		);

		sendAddShippingInfoEvent(checkoutState);

		const selectedPaymentType =
			checkoutState.context.data?.selectedPayment?.selectedPaymentType;
		// since payment methods with ComponentState
		// means we would need to go through the pain of getting the valid state for
		// the payment component before we can send the add_payment_info event
		// we defer it to be sent when the payment is initiated since now or later is the same thing
		if (
			!hasSentAddPaymentInfo &&
			!isPaymentTypeWithComponentState(selectedPaymentType) &&
			checkoutState.context.data
		) {
			pushToGTM({
				event: 'add_payment_info',
				ecommerce: {
					payment_type:
						checkoutState.context.data.selectedPayment?.selectedPaymentType ??
						'',
					...getCartValueAndItems({
						cart: checkoutState.context.data,
						marketCode,
					}),
				},
			});
			setHasSentAddPaymentInfo(true);
		}
	}, [
		checkoutService,
		hasSentAddPaymentInfo,
		marketCode,
		pushToGTM,
		sendAddShippingInfoEvent,
	]);

	const sendAddPaymentInfo = useCallback(() => {
		ignorePromiseRejection(sendAddPaymentInfoOnIdle());
	}, [sendAddPaymentInfoOnIdle]);

	const cart = useSelector(checkoutService, selectCartData);

	useEffect(() => {
		if (isInitiatingPayment) {
			if (!hasSentAddShippingInfo && cart) {
				pushToGTM({
					event: 'add_shipping_info',
					ecommerce: {
						shipping_tier: cart.selectedDelivery?.deliveryMethodId || '',
						...getCartValueAndItems({
							cart,
							marketCode,
						}),
					},
				});
			}
			if (!hasSentAddPaymentInfo && cart) {
				pushToGTM({
					event: 'add_payment_info',
					ecommerce: {
						payment_type: cart.selectedPayment?.selectedPaymentType ?? '',
						...getCartValueAndItems({
							cart,
							marketCode,
						}),
					},
				});
			}
		}
	}, [
		cart,
		hasSentAddPaymentInfo,
		hasSentAddShippingInfo,
		isInitiatingPayment,
		marketCode,
		pushToGTM,
	]);

	return {
		sendAddShippingInfo,
		sendAddPaymentInfo,
	};
}

export function useViewItemGTM() {
	const { pushToGTM } = useGtm();
	const marketCode = useMarketCode();

	const sendViewItemEvent = useCallback(
		({
			category,
			itemListId,
			itemListName,
			product,
		}: {
			category: string;
			itemListId: GTMItemListId | undefined;
			itemListName: GTMItemListName | undefined;
			product: GTMPriceProduct;
		}) => {
			pushToGTM({
				event: 'view_item',
				ecommerce: {
					currency: MARKET_CURRENCIES[marketCode],
					items: getItemsWithPrice({
						product,
						quantity: 1,
						itemListId,
						itemListName,
					}),
					category,
				},
			});
		},
		[marketCode, pushToGTM],
	);
	const sendViewItemEventOnce = useCallbackOnce(sendViewItemEvent);

	return { sendViewItemEvent, sendViewItemEventOnce };
}

export function useSearchTermGTM() {
	const { pushToGTM } = useGtm();

	const sendSearchTermCategoryViewEvent = useCallback(() => {
		pushToGTM({
			event: 'search_term_category_view',
		});
	}, [pushToGTM]);

	const sendSearchTermBrandViewEvent = useCallback(() => {
		pushToGTM({
			event: 'search_term_brand_view',
		});
	}, [pushToGTM]);

	const sendSearchTermCategorySelectEvent = useCallback(() => {
		pushToGTM({
			event: 'search_term_category_select',
		});
	}, [pushToGTM]);

	const sendSearchTermBrandSelectEvent = useCallback(() => {
		pushToGTM({
			event: 'search_term_brand_select',
		});
	}, [pushToGTM]);

	return {
		sendSearchTermBrandSelectEvent,
		sendSearchTermBrandViewEvent,
		sendSearchTermCategorySelectEvent,
		sendSearchTermCategoryViewEvent,
	};
}

export function useOrderHistoryEndGTM() {
	const { pushToGTM } = useGtm();

	const sendOrderHistoryEndEvent = useCallback(
		// TODO: Use branded date type?
		(date: string) => {
			pushToGTM({
				event: 'order_history_end',
				date,
			});
		},
		[pushToGTM],
	);

	return { sendOrderHistoryEndEvent };
}

export function useViewCartGTM() {
	const { pushToGTM } = useGtm();

	const sendViewCartEvent = useCallback(
		({
			cartId,
			rows,
		}: {
			cartId: CartId | undefined;
			rows: BasketRowResponse[];
		}) => {
			if (!cartId) {
				return;
			}
			pushToGTM({
				event: 'view_cart',
				ecommerce: {
					cart_id: cartId,
					items: getCartItemsWithPrice(rows),
				},
			});
		},
		[pushToGTM],
	);

	return { sendViewCartEvent };
}

export function useB2bB2cSwitchGTM() {
	const { pushToGTM } = useGtm();

	const sendB2bB2cSwitchEvent = useCallback(() => {
		pushToGTM({
			event: 'b2b_b2c_switch',
		});
	}, [pushToGTM]);

	return { sendB2bB2cSwitchEvent };
}

export function useOpenCreditSimulationGTM() {
	const { pushToGTM } = useGtm();

	const sendOpenCreditSimulationEvent = useCallback(() => {
		pushToGTM({
			event: 'open_credit_simulation',
		});
	}, [pushToGTM]);

	return { sendOpenCreditSimulationEvent };
}

export function useNotifyInStockGTM() {
	const { pushToGTM } = useGtm();

	const sendNotifyInStockEvent = useCallback(
		(productId: VariantId) => {
			pushToGTM({
				event: 'notify_in_stock',
				ecommerce: {
					items: [
						{
							item_id: productId,
						},
					],
				},
			});
		},
		[pushToGTM],
	);

	return { sendNotifyInStockEvent };
}
export function usePromptUserDetailsGTM() {
	const { pushToGTM } = useGtm();

	const sendPromptUserDetailsDisplayEvent = useCallback(() => {
		pushToGTM({
			event: 'prompt_user_details_display',
		});
	}, [pushToGTM]);

	const sendPromptUserDetailsDismissEvent = useCallback(() => {
		pushToGTM({
			event: 'prompt_user_details_dismiss',
		});
	}, [pushToGTM]);

	const sendPromptUserDetailsUpdateEvent = useCallback(() => {
		pushToGTM({
			event: 'prompt_user_details_update',
		});
	}, [pushToGTM]);

	const sendPromptNoAddressDisplayEvent = useCallback(() => {
		pushToGTM({
			event: 'prompt_no_address_display',
		});
	}, [pushToGTM]);

	const sendPromptNoAddressDismissEvent = useCallback(() => {
		pushToGTM({
			event: 'prompt_no_address_dismiss',
		});
	}, [pushToGTM]);

	const sendPromptNoAddressUpdateEvent = useCallback(() => {
		pushToGTM({
			event: 'prompt_no_address_update',
		});
	}, [pushToGTM]);

	return {
		sendPromptUserDetailsUpdateEvent,
		sendPromptUserDetailsDisplayEvent,
		sendPromptUserDetailsDismissEvent,
		sendPromptNoAddressDisplayEvent,
		sendPromptNoAddressDismissEvent,
		sendPromptNoAddressUpdateEvent,
	};
}

export function useSelectStoreOpenGTM() {
	const { pushToGTM } = useGtm();

	const sendSelectStoreOpenEvent = useCallback(() => {
		pushToGTM({
			event: 'select_store_open',
		});
	}, [pushToGTM]);

	return { sendSelectStoreOpenEvent };
}

export function useConfiguratorGTM() {
	const { pushToGTM } = useGtm();

	const sendConfiguratorOpenEvent = useCallback(
		(configuratorName: string) => {
			pushToGTM({
				event: 'configurator_open',
				configurator_name: configuratorName,
			});
		},
		[pushToGTM],
	);

	const sendConfiguratorProductListOpenEvent = useCallback(
		(configuratorName: string) => {
			pushToGTM({
				event: 'configurator_product_list_open',
				configurator_name: configuratorName,
			});
		},
		[pushToGTM],
	);

	const sendConfiguratorAddProductEvent = useCallback(
		(configuratorName: string, productId: string) => {
			pushToGTM({
				event: 'configurator_add_product',
				configurator_name: configuratorName,
				ecommerce: {
					items: [
						{
							item_id: productId,
						},
					],
				},
			});
		},
		[pushToGTM],
	);

	const sendConfiguratorSummaryOpenEvent = useCallback(
		(configuratorName: string) => {
			pushToGTM({
				event: 'configurator_summary_open',
				configurator_name: configuratorName,
			});
		},
		[pushToGTM],
	);

	const sendConfiguratorCloseEvent = useCallback(
		(configuratorName: string) => {
			pushToGTM({
				event: 'configurator_close',
				configurator_name: configuratorName,
			});
		},
		[pushToGTM],
	);

	return {
		sendConfiguratorOpenEvent,
		sendConfiguratorProductListOpenEvent,
		sendConfiguratorAddProductEvent,
		sendConfiguratorSummaryOpenEvent,
		sendConfiguratorCloseEvent,
	};
}

export function usePromotionGTM() {
	const { pushToGTM } = useGtm();

	const sendPromotion = useCallback(
		({
			event,
			id,
			name,
		}: {
			event: 'select_promotion' | 'view_promotion';
			id: GTMPromotionId;
			name: GTMPromotionName;
		}) => {
			pushToGTM({
				event,
				promotion_id: id,
				promotion_name: name,
			});
		},
		[pushToGTM],
	);

	const sendViewPromotionEvent = useCallback(
		(promotion: { id: GTMPromotionId; name: GTMPromotionName }) => {
			sendPromotion({
				event: 'view_promotion',
				...promotion,
			});
		},
		[sendPromotion],
	);

	const sendSelectPromotionEvent = useCallback(
		(promotion: { id: GTMPromotionId; name: GTMPromotionName }) => {
			sendPromotion({
				event: 'select_promotion',
				...promotion,
			});
		},
		[sendPromotion],
	);

	return { sendSelectPromotionEvent, sendViewPromotionEvent };
}

export function useSelectStoreChoiceGTM() {
	const { pushToGTM } = useGtm();

	const sendSelectStoreChoiceEvent = useCallback(
		({ storeName, storeStock }: { storeName: string; storeStock: number }) => {
			pushToGTM({
				event: 'select_store_choice',
				payload: {
					store_name: storeName,
					store_stock: storeStock,
				},
			});
		},
		[pushToGTM],
	);

	return { sendSelectStoreChoiceEvent };
}

export function useProfilingGTM() {
	const { pushToGTM } = useGtm();

	const sendProfilingEvent = useCallback(
		(
			variantId: VariantId,
			eventName: `profiling_${'add' | 'complete' | 'placement' | 'size' | 'start' | 'upload'}`,
		) => {
			pushToGTM({
				event: eventName,
				ecommerce: {
					items: [
						{
							item_id: variantId,
						},
					],
				},
			});
		},
		[pushToGTM],
	);

	const sendProfilingAddEvent = useCallback(
		(variantId: VariantId) => {
			sendProfilingEvent(variantId, 'profiling_add');
		},
		[sendProfilingEvent],
	);

	const sendProfilingStartEvent = useCallback(
		(variantId: VariantId) => {
			sendProfilingEvent(variantId, 'profiling_start');
		},
		[sendProfilingEvent],
	);

	const sendProfilingCompleteEvent = useCallback(
		(variantId: VariantId) => {
			sendProfilingEvent(variantId, 'profiling_complete');
		},
		[sendProfilingEvent],
	);

	const sendProfilingPlacementEvent = useCallback(
		(variantId: VariantId) => {
			sendProfilingEvent(variantId, 'profiling_placement');
		},
		[sendProfilingEvent],
	);

	const sendProfilingSizeEvent = useCallback(
		(variantId: VariantId) => {
			sendProfilingEvent(variantId, 'profiling_size');
		},
		[sendProfilingEvent],
	);
	const sendProfilingUploadEvent = useCallback(
		(variantId: VariantId) => {
			sendProfilingEvent(variantId, 'profiling_upload');
		},
		[sendProfilingEvent],
	);
	const sendProfilingPurchaseEvent = useCallback(
		(rows: BasketRowResponse[]) => {
			const profiledRows = rows.filter(
				(row) => row.customization?.customization?.id,
			);
			if (is.arrayWithLength(profiledRows)) {
				pushToGTM({
					event: 'profiling_purchase',
					ecommerce: {
						items: profiledRows.map((row) => ({
							item_id: row.variantId,
						})),
					},
				});
			}
		},
		[pushToGTM],
	);

	return {
		sendProfilingAddEvent,
		sendProfilingStartEvent,
		sendProfilingCompleteEvent,
		sendProfilingPlacementEvent,
		sendProfilingSizeEvent,
		sendProfilingUploadEvent,
		sendProfilingPurchaseEvent,
	};
}

export function usePurchaseGTM() {
	const { pushToGTM } = useGtm();
	const marketCode = useMarketCode();

	const sendPurchaseEvent = useCallback(
		(cart: Cart) => {
			pushToGTM({
				event: 'purchase',
				ecommerce: getPurchaseData({ cart, marketCode }),
			});
		},
		[marketCode, pushToGTM],
	);

	return { sendPurchaseEvent };
}

export function useLogoutGTM() {
	const { pushToGTM } = useGtm();

	const sendLogoutEvent = useCallback(() => {
		pushToGTM({
			event: 'logout',
		});
	}, [pushToGTM]);

	return { sendLogoutEvent };
}

export function useUserTypeUpdateGTM() {
	const { pushToGTM } = useGtm();

	const sendUserTypeUpdateEvent = useCallback(() => {
		pushToGTM({
			event: 'user_type_update',
		});
	}, [pushToGTM]);

	return { sendUserTypeUpdateEvent };
}

/**
 * For now both of these hooks have special behavior relating to the voyado id.
 * In both cases it's about the possibility of the token not having the id set.
 * Both are however at different stages of the login flow. The login event
 * does not have a user in context and needs to fetch all data, whereas the renewal event
 * happens when we already
 */
export function useLoginGTM() {
	const { pushToGTM } = useGtm();
	const getVoyadoIdAndSendLoginEvent = useCallback(async () => {
		let voyadoId: VoyadoId | undefined;
		try {
			const response = await fetch('/api/token/introspection/');
			if (response.ok) {
				const introspectionData =
					(await response.json()) as IntrospectionResponse;
				voyadoId = introspectionData.customerExtVoyado;
			}
		} catch {
			// a JSON parsing might go wrong
		}

		if (!voyadoId) {
			const customerData = await fetchEndpointResult('GetCustomer');
			if (customerData.isSuccess()) {
				voyadoId = customerData.value?.voyadoContactId;
			}
		}

		pushToGTM({
			event: 'login',
			voyado_id: voyadoId ?? '',
		});
	}, [pushToGTM]);

	const sendLoginEvent = useCallback(
		() => ignorePromiseRejection(getVoyadoIdAndSendLoginEvent()),
		[getVoyadoIdAndSendLoginEvent],
	);

	return { sendLoginEvent };
}

export function useLoginRenewalGTM() {
	const { pushToGTM } = useGtm();
	const { voyadoContactId } = useUserContext();

	const sendLoginRenewalEvent = useCallback(() => {
		pushToGTM({
			event: 'login_renewal',
			voyado_id: voyadoContactId ?? '',
		});
	}, [pushToGTM, voyadoContactId]);

	return { sendLoginRenewalEvent };
}

type ErrorType =
	| 'type_missing'
	| CartErrorType
	| ValidationErrorKey
	| FieldError['type'];

export function getErrorCode(key: ErrorType): GTMValidationErrorCode {
	const k = key.toLowerCase();

	if (k.endsWith('required')) return 'required';

	if (k.includes('length')) return 'length';

	if (/invalidchar|onlynumbers|^pattern$/.test(k)) return 'pattern';

	if (/notfound|notexist|invalid|validation|format/.test(k)) return 'invalid';

	log('sendValidationErrorEvent generic error_type for:', key);
	return 'generic';
}

export function useValidationErrorGTM() {
	const { pushToGTM } = useGtm();

	const sendValidationErrorEvent = useCallback(
		(formLocation: string, fieldName: string, errorType: ErrorType) => {
			pushToGTM({
				event: 'validation_error',
				form_field: `${snakeCase(formLocation)}.${snakeCase(fieldName)}`,
				error_code: getErrorCode(errorType),
			});
		},
		[pushToGTM],
	);

	return { sendValidationErrorEvent };
}

export function useIdentificationPromptGTM() {
	const { pushToGTM } = useGtm();

	const prompt = useCallback(() => {
		pushToGTM({ event: 'identification_prompt' });
	}, [pushToGTM]);

	const promptSuccess = useCallback(() => {
		pushToGTM({ event: 'identification_prompt_success' });
	}, [pushToGTM]);

	return { prompt, promptSuccess };
}
